import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../modules/layout/components/ToastContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAxiosGetResponse, IInstitutionDto } from "../../interfaces/IUtilities";
import { create, getById, update } from "../_request";
import { AxiosResponse } from "axios";
import * as Yup from "yup";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import { Formik, Form, Field } from "formik";
import Loader from "../../modules/layout/components/Loader";
import BackButton from "../../modules/layout/components/BackButton";
import { userHasRole } from "../../../setup/auth/authService";
import { AdminWeb } from "../../constants/constants";

const InstitutionForm: React.FC = () => {

    const {id} = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const {showToast} = useToast();
    const [loading, setLoading] = useState(true);
    const {t} = useTranslation();
    const [isAdminWeb, setIsAdminWeb] = useState(false);

    const [initialValues, setInitialValues] = useState<IInstitutionDto>({
            id: '00000000-0000-0000-0000-000000000000',
            name: '',
        });

        useEffect(() => {
                const fetchData = async () => {
                    try {
                        let result = await getById("/api/institutions/", id!);
                        let respStatus = result?.status || result?.response.status;
        
                        if (respStatus === 200) {
                            setInitialValues(result.data);
                            setLoading(false);
                        } else if (respStatus === 403) {
                            navigate("/error/403");
                        } else if (respStatus === 401) {
                            navigate("/auth/login");
                        } else if (respStatus === 500) {
                            navigate("/error/500");
                        } else {
                            throw new Error(t('StatusCode', {code: String(respStatus)}));
                        }
                    } catch (error) {
                        setLoading(false);
                        console.log(t('ConnectionError', {error: String(error)}));
                        showToast(t('ConnectionError', {error: String(error)}), 'error');
                        navigate("/institutions/list");
                    }
                }

                fetchRole();

                if (isEdit && id) {
                    fetchData();
                } else {    
                    setLoading(false);
                }
            }, [id, isEdit]);
        
            const handleSubmit = async (values: IInstitutionDto, { setErrors }: any) => {
                setLoading(true);
                if (isEdit) {
                    try {
                        let result = await update('/api/institutions/', {
                            ...values,
                        }) as AxiosResponse;
                        if (result.status === 200) {
                            setLoading(false);
                            showToast(t('InstitutionUpdatedSuccessfully'), 'success');
                            navigate("/institutions/list");
                        } else if (result.status === 400 && result.data?.errors) {
                            setLoading(false);
        
                            const formikErrors: Record<string, string> = {};
                            for (const key in result.data.errors) {
                                formikErrors[key] = t(result.data.errors[key]);
                            }
        
                            setErrors(formikErrors);
                        } else if (result.status === 403) {
                            navigate('/error/403');
                        } else if (result.status === 401) {
                            navigate('/auth/login');
                        } else if (result.status === 500) {
                            navigate('/error/500');
                        } else {
                            throw new Error(t('StatusCode', {code: String(result.status)}));
                        }
                    } catch (error) {
                        setLoading(false);
                        showToast(t('ConnectionError', {error: String(error)}), 'error');
                    }
                } else {
                    try {
                        let result = await create('/api/institutions', {
                            ...values,
                        }) as AxiosResponse;
                        if (result.status === 200) {
                            setLoading(false);
                            showToast(t('InstitutionCreatedSuccessfully'), 'success');
                            navigate("/institutions/list");
                        } else if (result.status === 400 && result.data?.errors) {
                            setLoading(false);
        
                            const formikErrors: Record<string, string> = {};
                            for (const key in result.data.errors) {
                                formikErrors[key] = t(result.data.errors[key]);
                            }
        
                            setErrors(formikErrors);
                        } else if (result.status === 403) {
                            navigate('/error/403');
                        } else if (result.status === 401) {
                            navigate('/auth/login');
                        } else if (result.status === 500) {
                            navigate('/error/500');
                        } else {
                            throw new Error(t('StatusCode', {code: String(result.status)}));
                        }
                    } catch (error) {
                        setLoading(false);
                        showToast(t('ConnectionError', {error: String(error)}), 'error');
                    }
                }
            };
        
            const validationSchema = Yup.object({
                    name: Yup.string().required(t('Required')),
                });

            const fetchRole = async () => { 
                const hasRole = await userHasRole(AdminWeb);
                if (!hasRole)
                    navigate("/error/403");
            };


    return (
        <div className="container mt-4 gg-container-form">

            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('Institutions'), href: "/institutions/list"},
                    {title: isEdit ? t('EditInstitution') : t('CreateInstitution')} // Active page (no href)
                ]}/>

            <h3>{isEdit ? t('EditInstitution') :  t('CreateInstitution')}</h3>

            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}
                    enableReinitialize>
                {({errors, touched, isSubmitting}) => (
                    <Form>
                        {isSubmitting && (
                            <Loader />
                        )}

                        <div className="row g-3 mb-3">
                            <div className="col-md-12">
                                {/*name*/}
                                <div className="mb-3">
                                    <label className="form-label">{t('Name')}</label>
                                    <Field className="form-control" name="name"/>
                                    {touched.name && errors.name &&
                                        <div className="text-danger">{errors.name}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <BackButton goTo={"/institutions/list"}/>

                            <button type="submit" className="btn ms-3 gg-btn-submit">
                                {isEdit ? t('Update') : t('Create')}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    )
}
export default InstitutionForm
